import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Project } from '@/shared/types/project';
import { projectsApi } from '@/shared/api/projects';

interface ProjectState {
  // State
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  loaded: boolean; // true after first successful load (prevents infinite loop for 0-project users)
  error: string | null;

  // Actions
  loadProjects: () => Promise<void>;
  setCurrentProject: (project: Project) => void;
  clearCurrentProject: () => void;
  refreshProjects: () => Promise<void>;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      // Initial state
      projects: [],
      currentProject: null,
      isLoading: false,
      loaded: false,
      error: null,

      // Load all projects from API
      loadProjects: async () => {
        if (get().isLoading) return; // Prevent concurrent calls
        set({ isLoading: true, error: null });
        try {
          const projects = await projectsApi.getProjects();
          set({ projects, isLoading: false, loaded: true });

          // Auto-select first project if no current project
          const { currentProject } = get();
          if (!currentProject && projects.length > 0) {
            set({ currentProject: projects[0] });
          }

          // Validate current project still exists
          if (currentProject) {
            const exists = projects.some((p) => p.id === currentProject.id);
            if (!exists) {
              set({ currentProject: projects.length > 0 ? projects[0] : null });
            }
          }
        } catch (error) {
          console.error('Failed to load projects:', error);
          set({
            error: 'Failed to load projects',
            isLoading: false,
            loaded: true, // Mark as loaded even on error to prevent retry loop
          });
        }
      },

      // Set current project
      setCurrentProject: (project) => {
        set({ currentProject: project });
      },

      // Clear current project
      clearCurrentProject: () => {
        set({ currentProject: null });
      },

      // Refresh projects list (explicit user action, always fetches)
      refreshProjects: async () => {
        set({ loaded: false });
        await get().loadProjects();
      },
    }),
    {
      name: 'project-storage',
      partialize: (state) => ({
        currentProject: state.currentProject,
      }),
    }
  )
);
